import { useState, useCallback, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronRight,
  RotateCw,
  AlertTriangle,
  Activity,
  Route,
  Loader2,
  Zap,
  Database,
  Clock,
  User,
  Cpu,
} from "lucide-react";
import {
  getTestResults,
  getTestDetail,
  evaluateTest,
  setReference,
  calibrateTurns,
  clearReferenceProfile,
  getTurnCalibration,
  apiError,
} from "@/api";
import { mapTestSummary, formatDateTime, formatDuration, fmtNum, initials } from "@/lib/testFormat";
import { MetricCard } from "@/components/test/MetricCard";
import { SensorCharts } from "@/components/test/SensorCharts";
import { TestResultPanel, ReferenceResultPanel } from "@/components/test/TestResultPanel";
import { TestPicker, ResultPill } from "@/components/test/TestPicker";
import { TurnsTable } from "@/components/test/TurnsTable";
import { ReferenceProfilePanel } from "@/components/test/ReferenceProfilePanel";

export default function TestPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("testId");

  const [tests, setTests] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [busy, setBusy] = useState(null); // "evaluate" | "reference"
  const [message, setMessage] = useState(null);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [refMessage, setRefMessage] = useState(null);
  const [clearing, setClearing] = useState(false);

  const loadTests = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await getTestResults();
      setTests((data.results || []).map(mapTestSummary));
    } catch (e) {
      setListError(`Could not load tests: ${apiError(e)}`);
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const data = await getTurnCalibration();
      setProfile(data.profile || null);
    } catch (e) {
      setProfileError(apiError(e));
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (testId) => {
    if (!testId) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await getTestDetail(testId);
      setDetail(data);
    } catch (e) {
      setDetail(null);
      setDetailError(apiError(e));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTests();
    loadProfile();
  }, [loadTests, loadProfile]);

  useEffect(() => {
    setMessage(null);
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  // Auto-select the most recent test once the list arrives
  useEffect(() => {
    if (!selectedId && tests.length) {
      const firstReal = tests.find((t) => t.sampleCount > 0) || tests[0];
      setSearchParams({ testId: firstReal.id }, { replace: true });
    }
  }, [tests, selectedId, setSearchParams]);

  const selectTest = useCallback((id) => setSearchParams({ testId: id }), [setSearchParams]);

  const handleEvaluate = useCallback(async () => {
    if (!selectedId) return;
    setBusy("evaluate");
    setMessage(null);
    try {
      const data = await evaluateTest(selectedId);
      const text = data.isReference
        ? `${selectedId}: reference metrics saved`
        : `${selectedId}: ${data.result} (score ${data.overallScore}) saved to Firebase`;
      setMessage({ type: "success", text });
      await Promise.all([loadDetail(selectedId), loadTests()]);
    } catch (e) {
      setMessage({ type: "error", text: `Evaluate failed: ${apiError(e)}` });
    } finally {
      setBusy(null);
    }
  }, [selectedId, loadDetail, loadTests]);

  const handleToggleReference = useCallback(async () => {
    if (!selectedId || !detail) return;
    const next = !detail.test.isReference;
    setBusy("reference");
    setMessage(null);
    try {
      await setReference(selectedId, next);
      setMessage({
        type: "success",
        text: next
          ? `${selectedId} marked as reference data. Rebuild the reference profile to use it.`
          : `${selectedId} is no longer reference data.`,
      });
      await Promise.all([loadDetail(selectedId), loadTests()]);
    } catch (e) {
      setMessage({ type: "error", text: `Update failed: ${apiError(e)}` });
    } finally {
      setBusy(null);
    }
  }, [selectedId, detail, loadDetail, loadTests]);

  const handleRebuild = useCallback(async () => {
    setRebuilding(true);
    setRefMessage(null);
    try {
      const data = await calibrateTurns();
      setProfile(data.profile);
      setRefMessage({ type: "success", text: `Reference profile rebuilt from ${data.profile.referenceRuns} tests` });
      if (selectedId) loadDetail(selectedId);
    } catch (e) {
      setRefMessage({ type: "error", text: apiError(e) });
    } finally {
      setRebuilding(false);
    }
  }, [selectedId, loadDetail]);

  const handleClearProfile = useCallback(async () => {
    if (!window.confirm("Delete the saved reference profile? Candidates will be scored with default limits.")) return;
    setClearing(true);
    setRefMessage(null);
    try {
      await clearReferenceProfile();
      setProfile(null);
      setRefMessage({ type: "success", text: "Reference profile cleared. Default limits are used until you rebuild." });
      if (selectedId) loadDetail(selectedId);
    } catch (e) {
      setRefMessage({ type: "error", text: apiError(e) });
    } finally {
      setClearing(false);
    }
  }, [selectedId, loadDetail]);

  const referenceCount = useMemo(() => tests.filter((t) => t.isReference).length, [tests]);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-mono text-zinc-500 mb-2">
            <Link to="/" className="hover:text-yellow-500 transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-yellow-500">Test Analysis</span>
          </div>
          <h1 data-testid="test-page-heading" className="font-heading text-2xl sm:text-3xl tracking-tight font-bold text-white">
            Recorded Test Analysis
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Real hardware data from Firebase: pick a test to see its sensor data between start and stop, the turns found, and the evaluation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3">
            <TestPicker
              tests={tests}
              loading={listLoading}
              error={listError}
              selectedId={selectedId}
              onSelect={selectTest}
              onRefresh={loadTests}
            />
          </div>

          <div className="lg:col-span-9 min-w-0">
            {message && (
              <div
                data-testid="test-action-message"
                className={`mb-6 px-4 py-3 rounded-lg text-sm font-mono ${
                  message.type === "success"
                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                    : "bg-red-500/10 text-red-400 border border-red-500/20"
                }`}
              >
                {message.text}
              </div>
            )}

            {detailLoading && !detail ? (
              <LoadingState />
            ) : detailError ? (
              <div role="alert" data-testid="test-detail-error" className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 text-sm text-red-400 mb-8">
                Could not load {selectedId}: {detailError}
              </div>
            ) : !detail ? (
              <TestEmptyState loading={listLoading} />
            ) : (
              <TestDetail
                detail={detail}
                refreshing={detailLoading}
                busy={busy}
                onEvaluate={handleEvaluate}
                onToggleReference={handleToggleReference}
              />
            )}

            <ReferenceProfilePanel
              profile={profile}
              loading={profileLoading}
              error={profileError}
              referenceCount={referenceCount}
              onRefresh={loadProfile}
              onRebuild={handleRebuild}
              rebuilding={rebuilding}
              message={refMessage}
              onClear={handleClearProfile}
              clearing={clearing}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TestDetail({ detail, refreshing, busy, onEvaluate, onToggleReference }) {
  const { test, session, series, metrics, turnAnalysis, preview, savedResult } = detail;
  const isReference = test.isReference;
  const hasData = series?.length >= 3;
  const saved = hasData && savedResult && savedResult.state === "EVALUATED" ? savedResult : null;

  return (
    <div data-testid="test-detail">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-zinc-800 border border-white/10 flex items-center justify-center font-mono font-bold text-zinc-200">
            {initials(test.candidate?.name)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 data-testid="detail-candidate-name" className="text-xl font-heading font-bold text-white">
                {test.candidate?.name || "Unknown candidate"}
              </h2>
              <ResultPill result={test.result} />
              {refreshing && <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />}
            </div>
            <p className="text-xs font-mono text-zinc-500 mt-0.5">{test.testId}{test.rfid ? ` · RFID ${test.rfid}` : ""}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="toggle-reference-btn"
            onClick={onToggleReference}
            disabled={!!busy || !hasData}
            className={`inline-flex items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
              isReference
                ? "bg-sky-500/10 text-sky-300 border-sky-500/30 hover:bg-sky-500/20"
                : "bg-zinc-800 text-white border-white/10 hover:bg-zinc-700"
            }`}
          >
            {busy === "reference" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
            {isReference ? "Remove from reference" : "Mark as reference data"}
          </button>
          <button
            type="button"
            data-testid="evaluate-test-btn"
            onClick={onEvaluate}
            disabled={!!busy || !hasData}
            className="inline-flex items-center gap-2 bg-yellow-500 text-black font-bold py-2.5 px-4 rounded-lg hover:bg-yellow-400 transition-colors text-sm disabled:opacity-50"
          >
            {busy === "evaluate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {isReference ? "Save reference metrics" : saved ? "Re-evaluate & save" : "Evaluate & save"}
          </button>
        </div>
      </div>

      {/* Candidate + session */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <InfoCard
          title="Candidate Details"
          icon={User}
          testId="candidate-details-card"
          rows={[
            ["Name", test.candidate?.name],
            ["Age", test.candidate?.age != null ? `${test.candidate.age} years` : null],
            ["Licence", test.candidate?.licenseType],
            ["Vehicle", test.candidate?.vehicleType],
            ["Test centre", test.candidate?.testCenter],
            ["RFID", test.rfid],
          ]}
        />
        <InfoCard
          title="Test Session"
          icon={Clock}
          testId="session-details-card"
          rows={[
            ["Start", formatDateTime(session.startMs)],
            ["Stop", formatDateTime(session.stopMs)],
            ["Duration", formatDuration(session.durationMs)],
            ["Samples used", `${session.sampleCountUsed} of ${session.sampleCountRaw}${session.sampleCountReported != null ? ` (device: ${session.sampleCountReported})` : ""}`],
            ["Sample rate", session.sampleRateHz ? `${session.sampleRateHz} Hz` : null],
            ["Status", test.status],
          ]}
        />
      </div>

      {!hasData ? (
        <div className="bg-zinc-900 border border-white/10 rounded-xl p-10 text-center text-sm text-zinc-400 mb-8">
          <AlertTriangle className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
          Not enough sensor samples between start and stop to analyse this test.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <MetricCard
              icon={Route}
              label="Turns"
              value={`${turnAnalysis?.turnCount ?? 0}/${turnAnalysis?.expectedTurns ?? 4}`}
              unit="found"
              color="text-sky-400"
              testId="metric-turns"
            />
            <MetricCard icon={RotateCw} label="Yaw RMS" value={fmtNum(metrics?.yawRms, 1)} unit="°/s" color="text-yellow-500" testId="metric-yaw" />
            <MetricCard icon={AlertTriangle} label="Harsh Events" value={`${metrics?.harshCount ?? 0}`} unit="count" color="text-red-400" testId="metric-harsh" />
            <MetricCard icon={Activity} label="Roll RMS" value={fmtNum(metrics?.rollRms, 1)} unit="°/s" color="text-emerald-400" testId="metric-roll" />
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono text-zinc-500 mb-4">
            <span className="flex items-center gap-1"><Cpu className="w-3 h-3" /> accel: {session.accelUnits}{session.accelCountsPerG ? ` · ${session.accelCountsPerG} counts/g` : ""}</span>
            <span>rest baseline x/y/z: {session.accelBaseline?.x}/{session.accelBaseline?.y}/{session.accelBaseline?.z}</span>
            <span>Δt ≈ {session.dtSec}s</span>
          </div>

          <SensorCharts series={series} turns={turnAnalysis?.turns} threshold={turnAnalysis?.threshold} method={turnAnalysis?.method} />
          <TurnsTable turnAnalysis={turnAnalysis} />

          {isReference ? (
            <ReferenceResultPanel metrics={metrics} />
          ) : saved ? (
            <TestResultPanel result={saved} testId={test.testId} certificateUrl={saved.certificateUrl} />
          ) : (
            <TestResultPanel result={preview} testId={test.testId} isPreview />
          )}
        </>
      )}
    </div>
  );
}

function InfoCard({ title, icon: Icon, rows, testId }) {
  return (
    <div data-testid={testId} className="bg-zinc-900 border border-white/10 p-5 rounded-xl">
      <span className="text-xs uppercase tracking-[0.2em] font-mono text-zinc-400 mb-4 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" /> {title}
      </span>
      <div className="space-y-2.5">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between items-center gap-4">
            <span className="text-xs font-mono text-zinc-500">{label}</span>
            <span className="text-sm text-white font-medium text-right">{value ?? "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-2xl p-16 text-center mb-8">
      <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mx-auto mb-3" />
      <p className="text-sm text-zinc-400">Loading sensor data from Firebase...</p>
    </div>
  );
}

function TestEmptyState({ loading }) {
  return (
    <div data-testid="test-empty-state" className="bg-zinc-900 border border-white/10 rounded-2xl p-16 text-center mb-8">
      <Activity className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
      <h3 className="font-heading text-xl font-semibold text-white mb-2">{loading ? "Loading tests..." : "No test selected"}</h3>
      <p className="text-sm text-zinc-400 max-w-md mx-auto">Select a recorded test from the list to view its real sensor data and evaluation.</p>
    </div>
  );
}
